import { createContext, useCallback, useContext, useMemo, useState } from "react";
import type { ReactNode } from "react";
import { api } from "../api/client";

// Session posture for the SPA. The JWT lives in the mm_token httpOnly cookie and is unreadable from
// script, so the token is never held here. This tracks only whether a protected call is expected to
// succeed and the role reported at login (for the chrome). The cookie is the real gate: any 401 from
// a protected call demotes the session to anonymous.

type AuthStatus = "unknown" | "authenticated" | "anonymous";

// sessionStorage key holding the display role across a reload. Non sensitive; never the token.
const ROLE_KEY = "mm.role";

interface AuthState {
	status: AuthStatus;
	role: string | null;
	login: (email: string, password: string) => Promise<void>;
	logout: () => Promise<void>;
	// Called when a protected call returns 401: the cookie is gone or expired.
	markAnonymous: () => void;
}

const AuthContext = createContext<AuthState | null>(null);

// Read the stored role, tolerating a storage that throws (private mode, disabled cookies).
function readStoredRole(): string | null {
	try {
		return sessionStorage.getItem(ROLE_KEY);
	} catch {
		return null;
	}
}

function writeStoredRole(role: string | null) {
	try {
		if (role === null) sessionStorage.removeItem(ROLE_KEY);
		else sessionStorage.setItem(ROLE_KEY, role);
	} catch {
		// Storage unavailable; the in memory state still governs this tab.
	}
}

export function AuthProvider({ children }: { children: ReactNode }) {
	const stored = readStoredRole();
	const [role, setRole] = useState<string | null>(stored);
	// A stored role starts the session optimistic (cookie may still be valid); a protected call confirms or demotes it.
	const [status, setStatus] = useState<AuthStatus>(stored ? "unknown" : "anonymous");

	const login = useCallback(async (email: string, password: string) => {
		const token = await api.login(email, password);
		setRole(token.role);
		setStatus("authenticated");
		writeStoredRole(token.role);
	}, []);

	const logout = useCallback(async () => {
		try {
			await api.logout();
		} catch {
			// Clearing is best effort; the cookie may already be absent.
		}
		setRole(null);
		setStatus("anonymous");
		writeStoredRole(null);
	}, []);

	const markAnonymous = useCallback(() => {
		setRole(null);
		setStatus("anonymous");
		writeStoredRole(null);
	}, []);

	const value = useMemo<AuthState>(
		() => ({ status, role, login, logout, markAnonymous }),
		[status, role, login, logout, markAnonymous],
	);

	return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

// Session accessor. Throws if used outside the provider, which is a wiring error, not a runtime state.
export function useAuth(): AuthState {
	const ctx = useContext(AuthContext);
	if (!ctx) throw new Error("useAuth requires AuthProvider");
	return ctx;
}
